import {
  AdditiveBlending,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  InstancedMesh,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  Points,
  RepeatWrapping,
  ShaderMaterial,
  UniformsLib,
  UniformsUtils,
} from 'three';
import type { CanvasTexture, Material } from 'three';
import { t } from '../../i18n/i18n';
import { drawnTexture } from './canvasTexture';
import { buildCrowd, mulberry } from './crowd';
import type { Seat } from './crowd';
import { flashBurst, sceneTime } from './time';

const hallFloor = -1.1;
const seatWidth = 0.62;
/** Every so many seats along a row, an aisle runs up the stands instead. */
const aisleEvery = 11;

/** A tier of stands all round the hall: how many rows, how far out and how high the first one is, and each step. */
interface Tier {
  readonly rows: number;
  readonly distance: number;
  readonly height: number;
  readonly depth: number;
  readonly rise: number;
}

/** The lower bowl, from the floor by the ring; and the balcony, set back above it. */
const lowerBowl: Tier = { rows: 14, distance: 7, height: hallFloor, depth: 0.95, rise: 0.55 };
const balcony: Tier = { rows: 7, distance: 22.4, height: hallFloor + 14 * 0.55 + 1.9, depth: 0.95, rise: 0.62 };

/**
 * The hall around the ring: a dark floor; judges, the press and photographers at the ringside; a lower bowl and a
 * balcony of stands full of spectators, aisles running up them lit at every step; glowing boards by the ring and a
 * ribbon of light along the balcony's front; camera flashes.
 */
export function buildHall(crowdSize: number): Group {
  const hall = new Group();
  hall.name = 'Hall';

  const floor = new Mesh(new PlaneGeometry(90, 90), new MeshStandardMaterial({ color: 0x14100e, roughness: 0.55, metalness: 0.1 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = hallFloor;
  floor.receiveShadow = true;
  hall.add(floor);

  const aisleLights: number[] = [];
  const stepMaterial = new MeshStandardMaterial({ color: 0x241a16, roughness: 0.9 });
  const random = mulberry(11);
  // The seats nearest the ring fill first; the balcony takes whoever is left.
  const seats = [lowerBowl, balcony].flatMap((tier) => {
    const tierSeats: Seat[] = [];
    hall.add(stands(tier, stepMaterial, random, tierSeats, aisleLights));
    return tierSeats.sort(() => random() - 0.5);
  });
  const shuffled = seats.slice(0, crowdSize);
  hall.add(buildCrowd(shuffled));

  hall.add(ringside());
  hall.add(stepLights(aisleLights));
  hall.add(boards());
  hall.add(ribbon());
  hall.add(flashes(shuffled));
  return hall;
}

// One tier's steps all round the hall, with the seats on them and the lights along its aisles.
function stands(tier: Tier, material: Material, random: () => number, seats: Seat[], aisleLights: number[]): Group {
  const group = new Group();
  for (let side = 0; side < 4; side++) {
    const turn = (side * Math.PI) / 2;
    const sin = Math.sin(turn);
    const cos = Math.cos(turn);
    for (let row = 0; row < tier.rows; row++) {
      const distance = tier.distance + row * tier.depth;
      const height = tier.height + row * tier.rise;
      const width = distance * 2 - 1.4;
      const step = new Mesh(new BoxGeometry(width, tier.rise, tier.depth), material);
      step.position.set(sin * distance, height - tier.rise / 2, cos * distance);
      step.rotation.y = turn;
      step.receiveShadow = true;
      group.add(step);
      // Seats counted out from the middle of the side, so the aisles line up from row to row.
      const most = Math.floor((width / 2 - seatWidth / 2) / seatWidth);
      for (let i = -most; i <= most; i++) {
        const along = i * seatWidth;
        if (i % aisleEvery === 0) {
          const edge = distance - tier.depth / 2 + 0.06;
          aisleLights.push(sin * edge + cos * along, height + 0.02, cos * edge - sin * along);
          continue;
        }
        if (random() < 0.08) continue;
        const nudge = (random() - 0.5) * 0.1;
        seats.push({ x: sin * distance + cos * (along + nudge), y: height, z: cos * distance - sin * (along + nudge), turn: turn + Math.PI });
      }
    }
  }
  return group;
}

// Round the ring: a table on each side with the judges or the press behind it, their laptops glowing, and
// photographers crouched at the ring's edge, cameras up, flashing when a big punch lands.
function ringside(): Group {
  const group = new Group();
  group.name = 'Ringside';
  const tableMaterial = new MeshStandardMaterial({ color: 0x121216, roughness: 0.6 });
  const officials: Seat[] = [];
  const screens: Object3D[] = [];
  const photographers: Seat[] = [];
  const cameras: number[] = [];
  for (let side = 0; side < 4; side++) {
    const turn = (side * Math.PI) / 2;
    const sin = Math.sin(turn);
    const cos = Math.cos(turn);
    const at = (distance: number, along: number) => [sin * distance + cos * along, cos * distance - sin * along] as const;
    const table = new Mesh(new BoxGeometry(5, 0.75, 0.6), tableMaterial);
    const [tx, tz] = at(5.2, 0);
    table.position.set(tx, hallFloor + 0.375, tz);
    table.rotation.y = turn;
    group.add(table);
    for (let seat = -2; seat <= 2; seat++) {
      const [x, z] = at(5.65, seat * 0.95);
      // Sitting: only their heads and shoulders show over the table.
      officials.push({ x, y: hallFloor - 0.3, z, turn: turn + Math.PI });
      const screen = new Object3D();
      const [sx, sz] = at(5.3, seat * 0.95);
      screen.position.set(sx, hallFloor + 0.87, sz);
      screen.rotation.set(0, turn, 0);
      screen.rotateX(-0.25);
      screens.push(screen);
    }
    if (side % 2 === 0) {
      for (let i = -3; i <= 3; i += 2) {
        const [x, z] = at(4.05, i * 0.8);
        photographers.push({ x, y: hallFloor - 0.35, z, turn: turn + Math.PI });
        cameras.push(sin * 3.95 + cos * i * 0.8, 0.15, cos * 3.95 - sin * i * 0.8);
      }
    }
  }
  group.add(buildCrowd([...officials, ...photographers]));
  const glow = new InstancedMesh(new PlaneGeometry(0.34, 0.22), new MeshBasicMaterial({ color: new Color(0.55, 0.7, 1).multiplyScalar(2.2) }), screens.length);
  screens.forEach((screen, i) => {
    // Facing away from the ring, at whoever sits behind it.
    screen.rotateY(Math.PI);
    screen.updateMatrix();
    glow.setMatrixAt(i, screen.matrix);
  });
  glow.name = 'Laptops';
  group.add(glow);
  group.add(ringsideFlashes(cameras));
  return group;
}

// The photographers' flashes: a big punch sets several off at once, bright, right at the ring's edge.
function ringsideFlashes(positions: readonly number[]): Points {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  const seeds = new Float32Array(positions.length / 3).map((_, i) => (i * 0.618) % 1);
  geometry.setAttribute('seed', new BufferAttribute(seeds, 1));
  const material = new ShaderMaterial({
    uniforms: { time: sceneTime, burst: flashBurst },
    vertexShader: `
      attribute float seed;
      uniform float time;
      uniform float burst;
      varying float vFlash;
      void main() {
        float flicker = fract(sin(seed * 57.3 + floor(time * 10.0) * 3.71) * 43758.5);
        vFlash = step(1.0 - burst * 0.7, flicker) * burst;
        vec4 view = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * view;
        gl_PointSize = vFlash * 420.0 / -view.z;
      }`,
    fragmentShader: `
      varying float vFlash;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        gl_FragColor = vec4(vec3(8.0) * vFlash * smoothstep(0.5, 0.0, d), 1.0);
      }`,
    blending: AdditiveBlending,
    depthWrite: false,
    transparent: true,
  });
  const points = new Points(geometry, material);
  points.frustumCulled = false;
  points.name = 'Ringside flashes';
  return points;
}

// Small warm lights at the edge of every step up the aisles.
function stepLights(positions: readonly number[]): Points {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  const material = new ShaderMaterial({
    vertexShader: `
      void main() {
        vec4 view = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * view;
        gl_PointSize = 90.0 / -view.z;
      }`,
    fragmentShader: `
      void main() {
        float d = length(gl_PointCoord - 0.5);
        gl_FragColor = vec4(vec3(1.0, 0.62, 0.3) * 1.6 * smoothstep(0.5, 0.1, d), 1.0);
      }`,
    blending: AdditiveBlending,
    depthWrite: false,
    transparent: true,
  });
  const points = new Points(geometry, material);
  points.frustumCulled = false;
  points.name = 'Aisle lights';
  return points;
}

/**
 * A board of lights: its picture runs slowly sideways, repeated so many times across it, and flares up with the camera
 * flashes on a big moment.
 */
function ledBoard(picture: CanvasTexture, repeats: number, speed: number, brightness: number): ShaderMaterial {
  picture.wrapS = RepeatWrapping;
  const material = new ShaderMaterial({
    uniforms: UniformsUtils.merge([UniformsLib.fog, { map: { value: null }, time: { value: 0 }, burst: { value: 0 } }]),
    vertexShader: `
      varying vec2 vUv;
      #include <fog_pars_vertex>
      void main() {
        vUv = uv;
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: `
      uniform sampler2D map;
      uniform float time;
      uniform float burst;
      varying vec2 vUv;
      #include <fog_pars_fragment>
      void main() {
        vec3 picture = texture2D(map, vec2(vUv.x * ${repeats.toFixed(2)} + time * ${speed.toFixed(3)}, vUv.y)).rgb;
        gl_FragColor = vec4(picture * ${brightness.toFixed(2)} * (1.0 + burst * 0.8), 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
    fog: true,
  });
  material.uniforms.map.value = picture;
  material.uniforms.time = sceneTime;
  material.uniforms.burst = flashBurst;
  return material;
}

// The arena's words, one after another along a strip, the given colours in turn.
function words(background: [string, string], colours: readonly string[]): CanvasTexture {
  return drawnTexture(2048, 128, (g, w, h) => {
    const back = g.createLinearGradient(0, 0, 0, h);
    back.addColorStop(0, background[0]);
    back.addColorStop(1, background[1]);
    g.fillStyle = back;
    g.fillRect(0, 0, w, h);
    g.font = `700 ${h * 0.5}px "Russo One", sans-serif`;
    g.textBaseline = 'middle';
    const said = [t('game-title'), '★', t('game-subtitle'), '★', t('arena-night'), '★'];
    // The whole strip is one round of words, so it runs on without a seam.
    const widths = said.map((word) => g.measureText(word).width + 40);
    const scale = w / widths.reduce((sum, width) => sum + width, 0);
    let x = 20 * scale;
    said.forEach((word, i) => {
      g.fillStyle = colours[i % colours.length];
      g.save();
      g.translate(x, h / 2);
      g.scale(scale, 1);
      g.fillText(word, 0, 0);
      g.restore();
      x += widths[i] * scale;
    });
  });
}

// Glowing boards by the ring, all round the hall.
function boards(): Group {
  const group = new Group();
  const material = ledBoard(words(['#15204a', '#0a1026'], ['#ffffff', '#ffd24a']), 1, 0.02, 1.6);
  for (let side = 0; side < 4; side++) {
    const turn = (side * Math.PI) / 2;
    const board = new Mesh(new PlaneGeometry(13, 0.8), material);
    board.position.set(Math.sin(turn) * 6.6, 0.1, Math.cos(turn) * 6.6);
    board.rotation.y = turn + Math.PI;
    group.add(board);
  }
  return group;
}

// A ribbon of light along the front of the balcony, all the way round the hall.
function ribbon(): Group {
  const group = new Group();
  const distance = balcony.distance - balcony.depth / 2 - 0.05;
  const width = distance * 2;
  // Whole rounds of words across each side, so none is cut at a corner.
  const material = ledBoard(words(['#3a0a0a', '#1a0404'], ['#ffffff', '#ff5a3c', '#ffd24a']), Math.round(width / 14), -0.015, 2.2);
  for (let side = 0; side < 4; side++) {
    const turn = (side * Math.PI) / 2;
    const band = new Mesh(new PlaneGeometry(width, 0.9), material);
    band.position.set(Math.sin(turn) * distance, balcony.height - 0.55, Math.cos(turn) * distance);
    band.rotation.y = turn + Math.PI;
    group.add(band);
  }
  group.name = 'Balcony ribbon';
  return group;
}

// Phones going off in the stands: tiny white flashes that pop now and then.
function flashes(seats: readonly Seat[]): Points {
  const count = Math.min(420, seats.length);
  const positions = new Float32Array(count * 3);
  const seeds = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    const seat = seats[(i * 7919) % seats.length];
    positions.set([seat.x, seat.y + 1.05, seat.z], i * 3);
    seeds[i] = (i * 0.618) % 1;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('seed', new BufferAttribute(seeds, 1));
  const material = new ShaderMaterial({
    uniforms: { time: sceneTime, burst: flashBurst },
    vertexShader: `
      attribute float seed;
      uniform float time;
      uniform float burst;
      varying float vFlash;
      void main() {
        float cycle = fract(time * (0.08 + seed * 0.1) + seed * 17.0);
        vFlash = smoothstep(0.985, 0.992, cycle) * (1.0 - smoothstep(0.992, 1.0, cycle));
        // After a big moment half the hall goes off at once, flickering.
        float flicker = fract(sin(seed * 91.7 + floor(time * 16.0) * 7.13) * 43758.5);
        vFlash = max(vFlash, step(1.0 - burst * 0.45, flicker) * burst);
        vec4 view = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * view;
        gl_PointSize = vFlash * 260.0 / -view.z;
      }`,
    fragmentShader: `
      varying float vFlash;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        gl_FragColor = vec4(vec3(6.0) * vFlash * smoothstep(0.5, 0.0, d), 1.0);
      }`,
    blending: AdditiveBlending,
    depthWrite: false,
    transparent: true,
  });
  const points = new Points(geometry, material);
  points.frustumCulled = false;
  points.name = 'Camera flashes';
  return points;
}
